import * as React from 'react';
import { RotateCcwIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import Lightbox, { type ZoomRef } from 'yet-another-react-lightbox';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

type ImageLightboxItem = {
  src: string;
  alt: string;
  width?: number;
  height?: number;
};

type ImageLightboxLabels = {
  close: string;
  previous: string;
  next: string;
  zoomIn: string;
  zoomOut: string;
  resetZoom: string;
  enterFullscreen: string;
  exitFullscreen: string;
};

type ImageLightboxProps = {
  open: boolean;
  index: number;
  images: ImageLightboxItem[];
  labels: ImageLightboxLabels;
  onIndexChange: (index: number) => void;
  onOpenChange: (open: boolean) => void;
};

type ImageLightboxTriggerProps = Omit<
  React.ComponentProps<'button'>,
  'aria-label' | 'className' | 'type'
> & {
  label: string;
};

function ImageLightboxTrigger({ label, children, ...props }: ImageLightboxTriggerProps) {
  return (
    <button
      type="button"
      aria-label={label}
      className="h-full w-full cursor-zoom-in rounded-none border-0 bg-transparent p-0 outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      {...props}
    >
      {children}
    </button>
  );
}

function ZoomControls({
  zoom,
  minZoom,
  maxZoom,
  disabled,
  zoomIn,
  zoomOut,
  changeZoom,
  labels,
}: ZoomRef & { labels: ImageLightboxLabels }) {
  return (
    <>
      <button
        type="button"
        className="yarl__button"
        disabled={disabled || zoom >= maxZoom}
        title={labels.zoomIn}
        aria-label={labels.zoomIn}
        onClick={zoomIn}
      >
        <ZoomInIcon />
      </button>
      <button
        type="button"
        className="yarl__button"
        disabled={disabled || zoom <= minZoom}
        title={labels.zoomOut}
        aria-label={labels.zoomOut}
        onClick={zoomOut}
      >
        <ZoomOutIcon />
      </button>
      <button
        type="button"
        className="yarl__button"
        disabled={disabled || zoom === minZoom}
        title={labels.resetZoom}
        aria-label={labels.resetZoom}
        onClick={() => changeZoom(minZoom)}
      >
        <RotateCcwIcon />
      </button>
    </>
  );
}

function ImageLightbox({
  open,
  index,
  images,
  labels,
  onIndexChange,
  onOpenChange,
}: ImageLightboxProps) {
  return (
    <Lightbox
      open={open}
      index={index}
      close={() => onOpenChange(false)}
      slides={images}
      plugins={[Zoom, Fullscreen]}
      carousel={{ finite: true }}
      zoom={{ scrollToZoom: true, pinchZoomV4: true }}
      labels={{
        Close: labels.close,
        Previous: labels.previous,
        Next: labels.next,
        'Zoom in': labels.zoomIn,
        'Zoom out': labels.zoomOut,
        'Enter Fullscreen': labels.enterFullscreen,
        'Exit Fullscreen': labels.exitFullscreen,
      }}
      render={{
        buttonPrev: images.length > 1 ? undefined : () => null,
        buttonNext: images.length > 1 ? undefined : () => null,
        buttonZoom: (zoom) => <ZoomControls {...zoom} labels={labels} />,
      }}
      on={{
        view: ({ index: nextIndex }) => onIndexChange(nextIndex),
      }}
    />
  );
}

export {
  ImageLightbox,
  ImageLightboxTrigger,
  type ImageLightboxItem,
  type ImageLightboxLabels,
  type ImageLightboxProps,
  type ImageLightboxTriggerProps,
};
